/**
 * Utility functions for conversion tracking (Facebook Ads and TikTok Ads)
 */

/**
 * Extract fbclid from URL search parameters
 */
export function getFbclidFromUrl(url?: string): string | null {
  try {
    const urlToCheck = url || (typeof window !== 'undefined' ? window.location.href : '');
    const urlObj = new URL(urlToCheck);
    return urlObj.searchParams.get('fbclid');
  } catch (error) {
    console.warn('Error extracting fbclid from URL:', error);
    return null;
  }
}

/**
 * Convert fbclid to fbc format as required by Meta Conversion API
 * Format: fb.{subdomainIndex}.{creationTime}.{fbclid}
 */
export function convertFbclidToFbc(fbclid: string): string {
  if (!fbclid) {
    throw new Error('fbclid is required');
  }

  // Get current timestamp in milliseconds
  const creationTime = Date.now();

  // Subdomain index - use 1 as default for main domain
  const subdomainIndex = 1;

  return `fb.${subdomainIndex}.${creationTime}.${fbclid}`;
}

/**
 * Get user agent string for Conversion API
 */
export function getUserAgent(): string {
  if (typeof window === 'undefined') return '';
  return window.navigator.userAgent;
}

/**
 * Extract ttclid from URL search parameters
 */
export function getTtclidFromUrl(url?: string): string | null {
  try {
    const urlToCheck = url || (typeof window !== 'undefined' ? window.location.href : '');
    const urlObj = new URL(urlToCheck);
    return urlObj.searchParams.get('ttclid');
  } catch (error) {
    console.warn('Error extracting ttclid from URL:', error);
    return null;
  }
}

/**
 * Store fbclid in sessionStorage for persistence across the signup flow
 */
export function storeFbclid(fbclid: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem('curipod_fbclid', fbclid);
  } catch (error) {
    console.warn('Error storing fbclid:', error);
  }
}

/**
 * Store ttclid in sessionStorage for persistence across the signup flow
 */
export function storeTtclid(ttclid: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem('curipod_ttclid', ttclid);
  } catch (error) {
    console.warn('Error storing ttclid:', error);
  }
}

/**
 * Retrieve fbclid from sessionStorage
 */
export function getStoredFbclid(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem('curipod_fbclid');
  } catch (error) {
    console.warn('Error retrieving stored fbclid:', error);
    return null;
  }
}

/**
 * Retrieve ttclid from sessionStorage
 */
export function getStoredTtclid(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem('curipod_ttclid');
  } catch (error) {
    console.warn('Error retrieving stored ttclid:', error);
    return null;
  }
}

/**
 * Get fbclid from URL or storage, with preference for URL
 */
export function getFbclid(): string | null {
  const urlFbclid = getFbclidFromUrl();
  if (urlFbclid) {
    // Store it for later use
    storeFbclid(urlFbclid);
    return urlFbclid;
  }

  // Fallback to stored value
  return getStoredFbclid();
}

/**
 * Get ttclid from URL or storage, with preference for URL
 */
export function getTtclid(): string | null {
  const urlTtclid = getTtclidFromUrl();
  if (urlTtclid) {
    // Store it for later use
    storeTtclid(urlTtclid);
    return urlTtclid;
  }

  // Fallback to stored value
  return getStoredTtclid();
}

/**
 * Get both tracking parameters as an object
 */
export function getTrackingParams(): { fbclid?: string; ttclid?: string } {
  const fbclid = getFbclid();
  const ttclid = getTtclid();

  const params: { fbclid?: string; ttclid?: string } = {};
  if (fbclid) params.fbclid = fbclid;
  if (ttclid) params.ttclid = ttclid;

  return params;
}
